import InputField from '../shared/InputField';
import SelectField from '../shared/SelectField';
import { User, Activity, FileText, ArrowRight } from '../shared/icons';

const WelcomeStep = ({ details, onDetailsChange, onStart }) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md lg:max-w-lg animate-slide-up relative z-10">
        <div className="glass-panel p-6 sm:p-8 lg:p-10 rounded-3xl shadow-xl">
          <div className="relative">
            {/* Logo */}
            <div className="flex flex-col items-center mb-6 sm:mb-8">
              <img
                src="/logo.png"
                alt="Vohra Neuropsychiatry Centre"
                className="w-32 h-32 sm:w-40 sm:h-40 object-contain mb-4"
              />
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-center text-brand-navy-dark tracking-tight">
                Vohra Neuropsychiatry Centre
              </h1>
              <p className="text-brand-navy text-center mt-1 text-sm sm:text-base font-semibold">
                Clinical Assessment Tool
              </p>
            </div>

            {/* Medical Disclaimer */}
            <div className="bg-brand-navy-light/20 border border-brand-navy-light rounded-xl p-4 mb-6 sm:mb-8">
              <p className="text-xs sm:text-sm text-brand-neutral-700 leading-relaxed text-center">
                <strong className="text-brand-navy-dark">Professional Use Only:</strong> This assessment must be administered by a qualified healthcare professional. Results require clinical interpretation.
              </p>
            </div>

            <div className="space-y-4">
              <InputField
                icon={User}
                label="Patient Name"
                placeholder="Full legal name"
                value={details.patient}
                onChange={(v) => onDetailsChange({ ...details, patient: v })}
              />

              <InputField
                icon={User}
                label="Date of Birth"
                placeholder="MM/DD/YYYY"
                type="date"
                value={details.dob}
                onChange={(v) => onDetailsChange({ ...details, dob: v })}
              />

              <InputField
                icon={Activity}
                label="Doctor Name"
                placeholder="Supervising clinician"
                value={details.doctor}
                onChange={(v) => onDetailsChange({ ...details, doctor: v })}
              />

              <SelectField
                icon={FileText}
                label="Assessment Type"
                value={details.testType}
                onChange={(v) => onDetailsChange({ ...details, testType: v })}
                options={[
                  { value: 'ASRS', label: 'Adult ADHD Self-Report Scale (ASRS)' },
                  { value: 'BAI', label: 'Beck Anxiety Inventory (BAI)' },
                  { value: 'BDI', label: 'Beck Depression Inventory (BDI)' },
                  { value: 'BSDS', label: 'Bipolar Spectrum Diagnostic Scale (BSDS)' },
                  { value: 'BPRS', label: 'Brief Psychiatric Rating Scale (BPRS)' },
                  { value: 'CAPS5', label: 'Clinician-Administered PTSD Scale (CAPS-5)' },
                  { value: 'CAARS', label: 'Conners Adult ADHD Rating Scale (CAARS)' },
                  { value: 'CPRS', label: 'Conners Parent Rating Scale (CPRS-R)' },
                  { value: 'CTRS', label: 'Conners Teacher Rating Scale (CTRS-R)' },
                  { value: 'ISAA', label: 'Indian Scale for Assessment of Autism (ISAA)' },
                  { value: 'IAT', label: 'Internet Addiction Test (IAT)' },
                  { value: 'IGDS9SF', label: 'Internet Gaming Disorder Scale (IGDS9-SF)' },
                  { value: 'MDQ', label: 'Mood Disorder Questionnaire (MDQ)' },
                  { value: 'PID5', label: 'Personality Inventory for DSM-5 (PID-5)' },
                  { value: 'PANSS', label: 'Positive and Negative Syndrome Scale (PANSS)' },
                  { value: 'PCPTSD5', label: 'Primary Care PTSD Screen (PC-PTSD-5)' },
                  { value: 'ASSIST', label: 'WHO ASSIST v3.1 (Substance Screening)' },
                  { value: 'YBOCS', label: 'Yale-Brown OCD Scale (YBOCS)' },
                  { value: 'YMRS', label: 'Young Mania Rating Scale (YMRS)' }
                ]}
              />

              <button
                onClick={onStart}
                disabled={!details.patient || !details.dob || !details.doctor}
                className={`w-full py-4 sm:py-5 rounded-xl font-semibold text-base sm:text-lg flex items-center justify-center gap-2 sm:gap-3 transition-all duration-300 mt-6 relative overflow-hidden group ${
                  details.patient && details.dob && details.doctor
                    ? 'bg-blue-800 text-white shadow-lg hover:shadow-2xl hover:-translate-y-1 active:translate-y-0 ring-2 ring-blue-400/30 hover:ring-blue-500/50'
                    : 'bg-brand-neutral-200 text-brand-neutral-400 cursor-not-allowed'
                }`}
              >
                {details.patient && details.dob && details.doctor && (
                  <div className="absolute inset-0 bg-blue-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                )}
                <span className="relative z-10 font-bold tracking-wide">Begin Assessment</span>
                <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 animate-fade-in-slow">
          <p className="text-brand-neutral-500 text-xs sm:text-sm font-medium">
            © 2026 Vohra Neuropsychiatry Centre. All rights reserved.
          </p>
          <p className="text-brand-neutral-400 text-xs mt-1">
            Secure • Confidential • HIPAA Compliant
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeStep;
